import { LanguageContext } from "@axon-enterprise/i18n-helpers";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { mixpanelService, TEMP_MX_EVENTS, TEMP_MX_PROPERTIES } from "../../common/tracking/MixpanelService";
import {
  useFlowActions,
  useFlowMessages,
  useFlowState,
  useFlowEngine,
} from "../../hooks";
import { chat, sendVision } from "../../services/llm";
import { storylineContextManager } from "../../services/storylineContext";
import type { ChatMessage } from "../../services/types";
import { getLanguageNameForChatGPT } from "../../utils/languageMapper";
import { logger } from "../../utils/logger";
import { StepRenderer } from "../steps";

import { ChatInput } from "./ChatInput";
import { ChatPanel } from "./ChatPanel";

// Create component logger for ChatbotWidget
const chatbotWidgetLogger = logger.withComponent("ChatbotWidget");

/**
 * Stable empty object for default initialVariables.
 * Using a module-level constant prevents React from seeing a "new" object
 * on every render, which would cause infinite re-render loops in effects
 * that include initialVariables in their dependency arrays.
 */
const EMPTY_INITIAL_VARIABLES: Record<string, any> = {};

interface ChatbotWidgetProps {
  flowId?: string;
  initialVariables?: Record<string, any>;
  enableFlowMode?: boolean;
  fallbackWelcomeMessage?: string;
  onRequestClose?: () => void;
}

export const ChatbotWidget: React.FC<ChatbotWidgetProps> = ({
  flowId,
  initialVariables = EMPTY_INITIAL_VARIABLES,
  enableFlowMode = true,
  fallbackWelcomeMessage = __('Welcome! How can I help you today?'),
  onRequestClose,
}) => {
  // Flow engine hooks
  const {
    isFlowActive,
    currentStep,
    isLoading: flowLoading,
    inputVisibility,
    flowId: activeFlowId,
  } = useFlowState();
  const { loadAndStart, submitUserInput, selectChip } = useFlowActions();
  const { messages, addUserMessage, addBotMessage } = useFlowMessages();
  const engine = useFlowEngine();
  const { language } = useContext(LanguageContext);
  const startupInFlightRef = useRef<string | null>(null);
  const startedFlowRef = useRef<string | null>(null);

  // Legacy chat state for non-flow mode
  const [legacyMessages, setLegacyMessages] = useState<ChatMessage[]>([
    { from: "bot", text: __('Welcome! How can I help you today?') },
  ]);
  const [legacyLoading, setLegacyLoading] = useState(false);

  // Determine which mode we're in
  const isFlowMode = enableFlowMode && (flowId || isFlowActive);
  const currentMessages = isFlowMode ? messages : legacyMessages;
  const currentLoading = isFlowMode ? flowLoading : legacyLoading;
  const currentInputVisibility = isFlowMode ? inputVisibility : "both";

  // Legacy message management for non-flow mode
  const addLegacyMessage = useCallback((message: ChatMessage) => {
    setLegacyMessages(prevMessages => {
      // Always remove ALL temporary messages before adding any new message
      // This prevents duplicate messages and ensures clean state
      const filteredMessages = prevMessages.filter(msg => !msg.temporary);

      // If the new message is temporary, add it to the filtered list
      if (message.temporary) {
        return [...filteredMessages, message];
      }

      // For non-temporary messages, just add to the filtered list
      return [...filteredMessages, message];
    });
  }, []);

  // Legacy chat message handling for non-flow mode
  const sendLegacyChatMessage = useCallback(
    async (text: string, imageUrl?: string) => {
      setLegacyLoading(true);

      // Add temporary loading message
      const loadingMessage: ChatMessage = {
        from: "bot",
        text: "",
        isLoading: true,
        temporary: true,
      };
      addLegacyMessage(loadingMessage);

      try {
        // Convert language code to human-readable name for ChatGPT
        const languageName = getLanguageNameForChatGPT(language);
        
        const response = imageUrl
          ? await sendVision({ message: text, imageUrl }, { language: languageName })
          : await chat({ message: text }, { language: languageName });

        chatbotWidgetLogger.info("Chat response received", {
          responseId: response.response_id,
          sessionId: response.session_id,
          contentLength: response.content?.length || 0,
          usage: response.usage,
        });

        // Add actual response (this will automatically remove the temporary loading message)
        // FIXED: Use response.content instead of response.response
        addLegacyMessage({
          from: "bot",
          text: response.content, // FIXED: Changed from response.response to response.content
        });
      } catch (error) {
        chatbotWidgetLogger.error("Chat error:", error);
        // Add error message (this will automatically remove the temporary loading message)
        addLegacyMessage({
          from: "bot",
          text: __('Sorry, I encountered an error. Please try again.'),
        });
      } finally {
        setLegacyLoading(false);
      }
    },
    [addLegacyMessage, language]
  );

  const startFlowIfNeeded = useCallback(
    async (requestedFlowId: string, variables: Record<string, any>, source: 'prop' | 'message') => {
      if (!engine || !enableFlowMode) {
        return;
      }

      if (startupInFlightRef.current) {
        chatbotWidgetLogger.debug('[ChatbotWidget] Skipping flow start while another start is in flight', {
          source,
          requestedFlowId,
          inFlightFlowId: startupInFlightRef.current,
        });
        return;
      }

      if (activeFlowId === requestedFlowId || startedFlowRef.current === requestedFlowId) {
        chatbotWidgetLogger.debug('[ChatbotWidget] Skipping duplicate flow start request', {
          source,
          requestedFlowId,
          activeFlowId,
          startedFlowId: startedFlowRef.current,
          isFlowActive,
        });
        return;
      }

      startupInFlightRef.current = requestedFlowId;

      try {
        await loadAndStart(requestedFlowId, variables);
        startedFlowRef.current = requestedFlowId;
      } catch (error) {
        chatbotWidgetLogger.error("[ChatbotWidget] Failed to start flow:", error);
      } finally {
        if (startupInFlightRef.current === requestedFlowId) {
          startupInFlightRef.current = null;
        }
      }
    },
    [activeFlowId, enableFlowMode, engine, isFlowActive, loadAndStart]
  );

  useEffect(() => {
    mixpanelService.track(TEMP_MX_EVENTS.ChatbotOpened, {});
  }, []);

  useEffect(() => {
    if (!engine?.on || !engine?.off || !onRequestClose) {
      return;
    }

    const handleChipAction = (event: { action: string }) => {
      if (event.action === 'closeChat') {
        onRequestClose();
      }
    };

    engine.on('chipAction', handleChipAction);

    return () => {
      engine.off('chipAction', handleChipAction);
    };
  }, [engine, onRequestClose]);

  // Initialize flow if flowId is provided
  useEffect(() => {
    if (flowId) {
      startFlowIfNeeded(flowId, initialVariables, 'prop');
    }
  }, [flowId, initialVariables, startFlowIfNeeded]);

  // Handle Storyline postMessage integration
  useEffect(() => {
    // Only run if embedded in iframe
    if (window.parent === window) return;

    const handleMessage = (event: MessageEvent) => {
      // Validate message structure
      if (!event.data || typeof event.data !== "object") return;

      const { helpFlowId, mainStepId } = event.data;

      // Update Storyline context if we received valid data
      if (helpFlowId || mainStepId) {
        // Store in singleton for auto-injection in LLM calls
        storylineContextManager.setContext({
          ...(helpFlowId && { helpFlowId }),
          ...(mainStepId && { mainStepId }),
        });

        chatbotWidgetLogger.info("Storyline context updated", {
          helpFlowId,
          mainStepId,
        });

        // If we got a helpFlowId and don't have a flow active, load it
        // Only use helpFlowId if no flowId was provided via URL
        if (helpFlowId && enableFlowMode && !flowId) {
          startFlowIfNeeded(helpFlowId, initialVariables, 'message');
        }
      }
    };

    // Add message listener
    window.addEventListener("message", handleMessage);

    // Send widgetReady handshake to Storyline
    window.parent.postMessage("widgetReady", "*");

    if (process.env.NODE_ENV === "development") {
      chatbotWidgetLogger.info(
        "[ChatbotWidget] Sent widgetReady handshake to parent"
      );
    }

    // Cleanup
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [enableFlowMode, flowId, initialVariables, startFlowIfNeeded]);

  // Handle sending messages - routes to flow or legacy based on mode
  const handleSendMessage = useCallback(
    async (text: string, files?: File[]) => {
      if (!text.trim() && (!files || files.length === 0)) return;

      chatbotWidgetLogger.info("Sending message", {
        mode: isFlowMode ? "flow" : "legacy",
        isFlowActive,
        messageLength: text.length,
        hasFiles: !!(files && files.length > 0),
      });

      if (isFlowMode && isFlowActive) {
        // Flow mode: submit input to current step
        try {
          // Handle imageInput steps specially
          if (currentStep?.type === "imageInput" && files && files.length > 0) {
            const file = files[0];
            // Emit inputComplete event for ImageInputStep to handle
            if (engine) {
              engine.emit("inputComplete", { type: "image", file });
            }
            return;
          }

          // Add user message to display
          if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            reader.onload = e => {
              if (e.target?.result) {
                addUserMessage(
                  text.trim(),
                  e.target.result as string,
                  file.name,
                  file.size
                );
                // Submit to flow engine
                submitUserInput({
                  text: text.trim(),
                  imageUrl: e.target.result as string,
                  fileName: file.name,
                  fileSize: file.size,
                });
              }
            };
            reader.readAsDataURL(file);
            return;
          }

          addUserMessage(text.trim());
          await submitUserInput({ text: text.trim() });
        } catch (error) {
          chatbotWidgetLogger.error("[ChatbotWidget] Flow input error:", error);
          addBotMessage(
            __('Sorry, I encountered an error processing your input. Please try again.')
          );
        }
      } else {
        // Legacy mode: use original chat logic
        const userMessage: ChatMessage = {
          from: "user",
          text: text.trim(),
        };

        // Handle file attachments in legacy mode
        if (files && files.length > 0) {
          const file = files[0];
          userMessage.fileName = file.name;
          userMessage.fileSize = file.size;

          const reader = new FileReader();
          reader.onload = e => {
            if (e.target?.result) {
              userMessage.imageUrl = e.target.result as string;
              addLegacyMessage(userMessage);
              sendLegacyChatMessage(text, e.target.result as string);
            }
          };
          reader.readAsDataURL(file);
          return;
        }

        addLegacyMessage(userMessage);
        await sendLegacyChatMessage(text);
      }
    },
    [
      isFlowMode,
      isFlowActive,
      addUserMessage,
      submitUserInput,
      addBotMessage,
      addLegacyMessage,
      sendLegacyChatMessage,
      currentStep?.type,
      engine,
    ]
  );

  // Auto-scroll to bottom when new messages arrive
  const chatPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatPanelRef.current) {
      chatPanelRef.current.scrollTop = chatPanelRef.current.scrollHeight;
    }
  }, [currentMessages]);

  // Ensure chips are fully visible by scrolling after they render
  useEffect(() => {
    if (currentStep?.type === "chips" && chatPanelRef.current) {
      // Delay scroll to ensure chips have appeared (they have a 300ms visibility delay)
      const scrollTimer = setTimeout(() => {
        if (chatPanelRef.current) {
          chatPanelRef.current.scrollTop = chatPanelRef.current.scrollHeight;
          chatbotWidgetLogger.debug(
            "[ChatbotWidget] Forced scroll for chips step",
            {
              stepId: currentStep.id,
              scrollHeight: chatPanelRef.current.scrollHeight,
            }
          );
        }
      }, 350); // Slightly longer than chips' 300ms visibility delay

      return () => clearTimeout(scrollTimer);
    }
  }, [currentStep]);

  return (
    <div className="chatbot-widget">
      <div className="chatbot-container">
        <ChatPanel ref={chatPanelRef} messages={currentMessages} />

        {/* Render current step if in flow mode */}
        {isFlowMode && currentStep && (
          <StepRenderer
            step={currentStep}
            onChipSelect={async (chipId: string) => {
              try {
                selectChip(chipId);
                // Track button/chip prompt submit
                mixpanelService.track(TEMP_MX_EVENTS.ChatbotPromptSubmitted, {
                  [TEMP_MX_PROPERTIES.InputType]: 'button',
                  [TEMP_MX_PROPERTIES.PromptId]: chipId,
                  [TEMP_MX_PROPERTIES.WizardStep]: currentStep?.id,
                });
              } catch (error) {
                chatbotWidgetLogger.error(
                  "[ChatbotWidget] Chip select error:",
                  error
                );
              }
            }}
            onMessage={(message: any) => {
              // Handle step-specific messages
              if (message.from === "user") {
                addUserMessage(
                  message.text,
                  message.imageUrl,
                  message.fileName,
                  message.fileSize
                );
              } else {
                addBotMessage(message.text, currentStep.id, message.imageUrl);
                // Track bot response (assume generative by default)
                mixpanelService.track(TEMP_MX_EVENTS.ChatbotResponse, {
                  [TEMP_MX_PROPERTIES.ResponseType]: 'generative',
                  [TEMP_MX_PROPERTIES.WizardStep]: currentStep.id,
                });
              }
            }}
          />
        )}

        <ChatInput
          onSend={(text, files) => {
            // Track user text prompt
            if (text && text.trim().length > 0) {
              mixpanelService.track(TEMP_MX_EVENTS.ChatbotPromptSubmitted, {
                [TEMP_MX_PROPERTIES.InputType]: 'text',
                [TEMP_MX_PROPERTIES.PromptTextLength]: text.trim().length,
                [TEMP_MX_PROPERTIES.WizardStep]: currentStep?.id,
              });
            }
            handleSendMessage(text, files);
          }}
          isLoading={currentLoading}
          inputVisibility={currentInputVisibility}
          autoTriggerAction={
            currentStep?.type === "imageInput"
              ? (currentStep as any).source
              : undefined
          }
        />
      </div>
    </div>
  );
};
